<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getAllTrackingProperties } from '$lib/helper';
    import { isUrl } from '$lib/utils/isUrl';

    import { onMount, onDestroy } from 'svelte';

    import CalendarPopup from './CalendarPopup.svelte';
    import { CalendarProvider } from './constants';
    import Form from './FormQuestion.View.svelte';
    import { verifyOtpForSubmit } from './verifyOtpForSubmit';
    import { iclosed } from '../_utils/iclosed';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Box as BoxType, Align, Size } from '$lib/types/Components/SharedTypes';
    import type { FormValues } from '$lib/types/Form';
    import type { Language } from '$lib/types/Language';
    import type { OtpVerificationParams } from '$lib/types/OtpVerification';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';

    export let campaignId = '';

    // iClosed
    export let calendarUrl = '';
    export let provider = undefined;
    export let onIClosedFailed = () => console.error('iClosed could not be loaded...');
    export let onCalendarFormSubmit: (formValues: FormValues) => void = () => null;
    export let otpVerificationParams: OtpVerificationParams | null = null;

    // Question
    export let id = '';
    export let theme: Theme;
    export let align: Align = 'center';
    export let wysiwygHtml: string | undefined = undefined;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';

    // Inputs
    export let inputs: InputData[] = [];

    // Submit Button
    export let btnId = '';
    export let btnWysiwygHtml: string | undefined = undefined;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAlign: Align = 'center';
    export let btnFullWidthOnDesktop: boolean = false;
    export let btnSize: Size = 'medium';
    export let btnStyle: ButtonStyle = 'filled';
    export let loading = false;

    let savedFormValues: FormValues = {};
    let isPopupOpen = false;
    let iframeUrl = '';

    const isValidUrl =
        provider === CalendarProvider.ICLOSED
            ? iclosed.validateUrl(calendarUrl)
            : isUrl(calendarUrl);

    const expectedOrigin = isValidUrl ? new URL(calendarUrl).origin : '';

    onMount(() => {
        window.addEventListener('message', handleMessageEvent);
    });

    onDestroy(() => {
        window.removeEventListener('message', handleMessageEvent);
    });

    const openIClosedPopup = (formData: FormValues) => {
        if (!isValidUrl) {
            onIClosedFailed();
            return;
        }

        const allTrackingProperties = getAllTrackingProperties(campaignId);
        const namedAnswers = iclosed.extractNamedAnswers(inputs, formData);

        iframeUrl = iclosed.buildIframeUrl(calendarUrl, namedAnswers, allTrackingProperties);

        isPopupOpen = true;
    };

    const closePopup = () => {
        isPopupOpen = false;
    };

    const handleEventScheduled = () => {
        closePopup();
        onSubmit(savedFormValues);
    };

    const handleMessageEvent = (event: MessageEvent) => {
        if (!expectedOrigin || event.origin !== expectedOrigin) {
            return;
        }

        const data = event.data;

        if (data && typeof data === 'object' && iclosed.isBookingSuccessful(data)) {
            handleEventScheduled();
        }
    };

    // FUN-3876: Retry hint shown below the submit button when verification
    // could not be started and the submit fails closed
    let otpSubmitError: string | null = null;

    const handleSubmit = async (formData: FormValues) => {
        otpSubmitError = null;

        // FUN-3876: Verify before any tracking or navigation — also for the
        // invalid-URL fallback, which would otherwise bypass the OTP gate
        const verification = await verifyOtpForSubmit(
            otpVerificationParams,
            formData,
            language,
            'iClosed',
        );

        if (!verification.ok) {
            otpSubmitError = verification.errorMessage;

            return;
        }

        // FUN-3876: Track the form submit only after successful verification
        onCalendarFormSubmit(formData);

        if (isValidUrl) {
            savedFormValues = formData;

            return openIClosedPopup(formData);
        }

        return onSubmit(formData);
    };
</script>

<Form
    {id}
    {theme}
    {align}
    {wysiwygHtml}
    {color}
    {box}
    {inputs}
    {btnId}
    {btnWysiwygHtml}
    {btnBgColor}
    {btnFullWidthOnDesktop}
    {btnColor}
    {btnAlign}
    {btnStyle}
    {btnSize}
    {loading}
    {layout}
    {language}
    {onMouseEnter}
    {transitionEffect}
    {transitionDelay}
    onSubmit={handleSubmit}
    submitError={otpSubmitError}
/>

<CalendarPopup isOpen={isPopupOpen} {iframeUrl} onClose={closePopup} />
